import { FormEvent } from 'react';

import { ListingType } from 'algolia-api/types/ListingType';
import { SearchLocation } from 'algolia-api/types/SearchLocation';
import { StaffTypeFilter } from 'algolia-api/types/StaffTypeFilter';
import { PrimaryButton } from 'components/Button';
import { ListingTypeSelect } from 'components/Listing/ListingTypeSelect';
import { LocationInput } from 'components/LocationInput/LocationInput';
import { UncontrolledSearchQueryInputContainer } from 'containers/UncontrolledSearchQueryInputContainer';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { scrollToTop } from 'utils/scroll';

import {
  PageHeaderSearchDesktopInputsForm,
  PageHeaderSearchInputsDesktopFieldsHolder,
  PageHeaderSearchInputsDesktopListingTypeSelectHolder,
  PageHeaderSearchInputsDesktopLocationFooter,
  PageHeaderSearchInputsDesktopQueryInputHolder,
} from './PageHeaderSearchDesktopInputs.styled';

type DisplayedType = ListingType | StaffTypeFilter;

type Props = {
  selectedType: DisplayedType;
  listingTypes: Array<DisplayedType>;
  onSearch?: (event: FormEvent<HTMLFormElement>) => void;
  query: string;
  initialQuery: string;
  searchLocation: SearchLocation | null | undefined;
  setListingType: (arg0: ListingType) => void;
  setQuery: (arg0: string) => void;
  onLocationChange: (searchLocation: SearchLocation | null | undefined) => void;
  search: (arg0: string) => void;
  clearSearchInput: () => void;
  searchInputId: string;
  searchInputQaId: string;
};

export function PageHeaderSearchInputsDesktop({
  selectedType,
  listingTypes,
  onSearch,
  query,
  initialQuery,
  searchLocation,
  setListingType,
  setQuery,
  onLocationChange,
  search,
  clearSearchInput,
  searchInputId,
  searchInputQaId,
}: Props) {
  const { isEnvironmentLoaded } = useUserEnvironment();

  return (
    <PageHeaderSearchDesktopInputsForm
      onSubmit={(event) => {
        event.preventDefault();
        search(query);
        onSearch?.(event);
      }}
      data-qa-id="search-inputs"
    >
      <PageHeaderSearchInputsDesktopFieldsHolder>
        <PageHeaderSearchInputsDesktopListingTypeSelectHolder>
          <ListingTypeSelect
            value={selectedType}
            onChange={setListingType}
            listingTypes={listingTypes}
            variant="grey"
          />
        </PageHeaderSearchInputsDesktopListingTypeSelectHolder>

        <PageHeaderSearchInputsDesktopQueryInputHolder>
          <UncontrolledSearchQueryInputContainer
            initialQuery={initialQuery}
            placeholder={getText('Search by keyword, skill, or interest')}
            xsPlaceholder={getText('Keyword, skill, or interest')}
            onChange={setQuery}
            onClearQuery={clearSearchInput}
            presentation="contrast"
            id={searchInputId}
            qaId={searchInputQaId}
          />
        </PageHeaderSearchInputsDesktopQueryInputHolder>

        <LocationInput
          affixVariant="transparent"
          styleVariant="contrast"
          hasRemoteOptions
          showLocationIcon
          showClearButton
          placeholder={getText('Everywhere')}
          locationText={searchLocation?.text}
          onChange={onLocationChange}
        />
      </PageHeaderSearchInputsDesktopFieldsHolder>

      <PageHeaderSearchInputsDesktopLocationFooter>
        <PrimaryButton
          icon={{ type: 'normalized', name: 'search' }}
          type="submit"
          data-qa-id="search-button"
          dataReady={isEnvironmentLoaded}
          onClick={scrollToTop}
          height={48}
          fullWidth
        >
          {getText('Search')}
        </PrimaryButton>
      </PageHeaderSearchInputsDesktopLocationFooter>
    </PageHeaderSearchDesktopInputsForm>
  );
}
